/**
 * When a SCHEDULED order fires to the kitchen — the one rule, for every source.
 *
 * A scheduled order is a promise ("ready at 5:00 PM"). The kitchen has to START
 * it one prep lead earlier, so the order is HELD (`orderState: 'scheduled'`)
 * with `fireAt = scheduledTime − prepLead`, and the unified activator
 * (`orders where orderState == 'scheduled' && fireAt <= now`, every minute —
 * docs/UNIFIED_ORDER_ACCEPTANCE_ARCHITECTURE.md §6) opens it and fires the
 * kitchen at that instant.
 *
 * `fireAt` is persisted, not derived at read time, so the promise survives a
 * later prep-time edit (§6).
 *
 * Callers: first-party checkout (`apps/api` orders route), marketplace ingest
 * (`ingestion-orchestrator.ts`), and the till (`apps/pos`
 * `lib/models/order/scheduled_fire.dart`, a Dart copy pinned to this file by
 * `scheduledFireParity.test.ts` — the till cannot import this package).
 */

/** The STANDARD PREP TIME when a restaurant configured none (§6). */
export const DEFAULT_SCHEDULED_PREP_LEAD_MINUTES = 20;

/** The restaurant fields the prep lead is read from. */
export interface PrepLeadSource {
    readonly settings?: { readonly estimatedPrepTimeMinutes?: number | null } | null;
}

const usableMinutes = (value: number | null | undefined): number | undefined =>
    typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined;

/**
 * The restaurant's STANDARD PREP TIME — how long an ordinary order takes when
 * the kitchen is not busy, and how many minutes before its promised time a
 * scheduled order starts cooking.
 *
 * ONE field, restaurant-wide: `settings.estimatedPrepTimeMinutes`, else 20
 * (owner, 2026-09-25). How long the kitchen takes is a property of the kitchen,
 * never of the channel an order came from, so the website's ordering prep time
 * (`websiteConfig.ordering.config.prepTimeMinutes`) is NOT a second source —
 * the till (`scheduled_fire.dart`) and functions (`orderOverdue.ts`) read the
 * same one field. A negative, NaN or non-numeric value is skipped, never used:
 * a negative lead would fire the kitchen AFTER the promised time.
 */
export function resolvePrepLeadMinutes(locationRecord: PrepLeadSource | null | undefined): number {
    return (
        usableMinutes(locationRecord?.settings?.estimatedPrepTimeMinutes) ??
        DEFAULT_SCHEDULED_PREP_LEAD_MINUTES
    );
}

/**
 * The instant a scheduled order fires: `scheduledTime − prepLeadMinutes`,
 * clamped at `now` (a promise inside the prep lead is already late, and the
 * kitchen starting in the past is not a thing).
 *
 * `undefined` when there is nothing to hold — no scheduled time, or one that is
 * not in the future. A caller HOLDS the order only when the result is after
 * `now`; a result equal to `now` means "fire it now".
 */
export function computeFireAt(
    scheduledTime: Date | null | undefined,
    prepLeadMinutes: number,
    now: Date = new Date(),
): Date | undefined {
    if (!scheduledTime || Number.isNaN(scheduledTime.getTime())) return undefined;
    if (scheduledTime.getTime() <= now.getTime()) return undefined;
    const lead = usableMinutes(prepLeadMinutes) ?? DEFAULT_SCHEDULED_PREP_LEAD_MINUTES;
    return new Date(Math.max(scheduledTime.getTime() - lead * 60_000, now.getTime()));
}

/** True when an order with this `fireAt` must be HELD rather than fired now. */
export function shouldHoldUntil(fireAt: Date | null | undefined, now: Date = new Date()): boolean {
    return !!fireAt && fireAt.getTime() > now.getTime();
}

/**
 * The smallest move worth writing: a re-pace that would bring the fire in by
 * less than this is noise from a load that ticks up and down, and writing it
 * every minute would churn the order document for nothing.
 */
export const REPACE_MIN_STEP_MS = 60_000;

/**
 * Whether a HELD order is close enough to its promise that today's kitchen load
 * says anything about when it must start: its promise is within the longest
 * lead the kitchen can quote (`maxPrepMinutes`). Further out, the load now says
 * nothing about the load then, and the accept-time `fireAt` stands.
 */
export function withinRepaceHorizon(
    scheduledTime: Date,
    maxPrepMinutes: number,
    now: Date = new Date(),
): boolean {
    const max = usableMinutes(maxPrepMinutes) ?? 60;
    return scheduledTime.getTime() - max * 60_000 <= now.getTime();
}

/**
 * The fire time a HELD order moves to once the kitchen's LIVE lead is known —
 * `scheduledTime − liveLeadMinutes`, clamped at `now` — or `undefined` when it
 * should stay where it is.
 *
 * EARLIER ONLY (owner, 2026-09-29: "if all of a sudden there's a rush and the
 * scheduled order needed to start earlier, we should account for that"). The
 * accept-time `fireAt` used the standard lead with NO load — the least the
 * kitchen ever needs — so a calmer kitchen never needs a later start, and
 * moving it later could only ever make the promise late. A move smaller than
 * {@link REPACE_MIN_STEP_MS} is not written. An order already due is the
 * activator's, not this rule's.
 */
export function repacedFireAt(
    fireAt: Date,
    scheduledTime: Date,
    liveLeadMinutes: number,
    now: Date = new Date(),
): Date | undefined {
    const lead = usableMinutes(liveLeadMinutes);
    if (lead === undefined) return undefined;
    if (Number.isNaN(fireAt.getTime()) || Number.isNaN(scheduledTime.getTime())) return undefined;
    if (fireAt.getTime() <= now.getTime()) return undefined;
    const candidate = Math.max(scheduledTime.getTime() - lead * 60_000, now.getTime());
    if (candidate > fireAt.getTime() - REPACE_MIN_STEP_MS) return undefined;
    return new Date(candidate);
}
